import { useCallback, useRef } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PAGE_CONFIGS } from '__queries/query-keys';
import TeamMemberService from 'services/TeamMemberService';

const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const PAGE_CONFIG_KEY = 'billing';

type BillingPageConfig = {
  lastWarnFailing?: string;
  lastWarnShutdown?: string;
};

export const useBillingWarningDisplay = (enabled = true) => {
  const { data, isLoading } = useQuery({
    queryKey: PAGE_CONFIGS.BILLING_NOTICE,
    queryFn: () => TeamMemberService.getPageConfig(PAGE_CONFIG_KEY, {}),
    refetchOnWindowFocus: false,
    enabled,
  });

  const queryClient = useQueryClient();

  const configMutation = useMutation({
    mutationFn: (config: BillingPageConfig) => {
      return TeamMemberService.setPageConfig(PAGE_CONFIG_KEY, config);
    },
    onMutate: async (updated: BillingPageConfig) => {
      await queryClient.cancelQueries(PAGE_CONFIGS.BILLING_NOTICE);

      const previousData = queryClient.getQueryData(
        PAGE_CONFIGS.BILLING_NOTICE
      );

      queryClient.setQueryData(PAGE_CONFIGS.BILLING_NOTICE, updated);

      return { previousData };
    },
    onError: (_err, _updated, context) => {
      queryClient.setQueryData(
        PAGE_CONFIGS.BILLING_NOTICE,
        context?.previousData
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries(PAGE_CONFIGS.BILLING_NOTICE);
    },
  });

  const lastWarnFailing = data?.lastWarnFailing;
  const lastWarnShutdown = data?.lastWarnShutdown;

  const now = new Date();
  const warnFailingDate = lastWarnFailing ? new Date(lastWarnFailing) : null;
  const warnShutdownDate = lastWarnShutdown ? new Date(lastWarnShutdown) : null;

  const warnFailingElapsed = warnFailingDate
    ? now.getTime() - warnFailingDate.getTime()
    : 0;
  const warnShutdownElapsed = warnShutdownDate
    ? now.getTime() - warnShutdownDate.getTime()
    : 0;

  const canShowWarnFailing = warnFailingDate
    ? warnFailingElapsed >= TWENTY_FOUR_HOURS
    : true;

  const canShowWarnShutdown = warnShutdownDate
    ? warnShutdownElapsed >= TWENTY_FOUR_HOURS
    : true;

  const hasMarkedWarnFailingThisSession = useRef(false);
  const markWarnFailingShown = useCallback(() => {
    if (hasMarkedWarnFailingThisSession.current) {
      return;
    }

    hasMarkedWarnFailingThisSession.current = true;
    configMutation.mutate({
      ...data,
      lastWarnFailing: new Date().toISOString(),
    });
  }, [configMutation, data]);

  const hasMarkedWarnShutdownThisSession = useRef(false);
  const markWarnShutdownShown = useCallback(() => {
    if (hasMarkedWarnShutdownThisSession.current) {
      return;
    }

    hasMarkedWarnShutdownThisSession.current = true;
    configMutation.mutate({
      ...data,
      lastWarnShutdown: new Date().toISOString(),
    });
  }, [configMutation, data]);

  return {
    canShowWarnFailing,
    canShowWarnShutdown,
    markWarnFailingShown,
    markWarnShutdownShown,
    isLoading,
  };
};
